/**
 * Centralized onboarding step definitions
 * This enum serves as the single source of truth for all onboarding step names
 *
 * Usage examples:
 * - Direct access: ONBOARDING_STEP.BIRTHDAY
 * - Step name lookup: ONBOARDING_STEPS[1] // returns 'birthday'
 * - Type checking: stepName: OnboardingStepName
 */
export enum ONBOARDING_STEP {
  BIRTHDAY = 'birthday',
  ANNUAL_INCOME = 'annual_income',
  LIQUID_NET_WORTH = 'liquid_net_worth',
  TAX_FILING_LOCATION = 'tax_filing_location',
  FILING_STATUS = 'filing_status',
  RISK_PREFERENCE = 'risk_preference',
  PROJECT_MATCHING = 'project_matching',
  ACCREDITATION = 'accreditation',
  CELEBRATION = 'celebration',
  EMAIL = 'email',
}

/**
 * Mapping from step numbers to step names
 * This maintains backward compatibility with existing number-based lookups
 */
export const ONBOARDING_STEPS = {
  // 0: ONBOARDING_STEP.BIRTHDAY, // Disabled - not in use
  1: ONBOARDING_STEP.ANNUAL_INCOME,
  2: ONBOARDING_STEP.LIQUID_NET_WORTH,
  3: ONBOARDING_STEP.TAX_FILING_LOCATION,
  4: ONBOARDING_STEP.FILING_STATUS,
  5: ONBOARDING_STEP.RISK_PREFERENCE,
  6: ONBOARDING_STEP.ACCREDITATION,
  7: ONBOARDING_STEP.PROJECT_MATCHING,
  8: ONBOARDING_STEP.CELEBRATION,
  9: ONBOARDING_STEP.EMAIL,
} as const;

/**
 * Type for onboarding step names
 */
export type OnboardingStepName =
  | (typeof ONBOARDING_STEPS)[keyof typeof ONBOARDING_STEPS]
  | ONBOARDING_STEP;

/**
 * Interface for onboarding state data used to calculate total steps
 */
export interface OnboardingCalculationData {
  country?: string;
  // Placeholder for future conditional logic fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Calculate the total number of onboarding steps based on user responses
 * This determines whether to use different flows:
 * - Non-US flow: 4 steps (Steps 1-3 + Step 4 RequestAccess), then redirect to /waitlist-sent
 * - US flow: 7 steps (full flow with accreditation always enabled)
 *
 * @param data - User onboarding data containing country
 * @returns The total number of steps for the current onboarding flow
 */
export const calculateTotalOnboardingSteps = (
  data?: OnboardingCalculationData
): number => {
  if (!data) {
    return DEFAULT_TOTAL_ONBOARDING_STEPS;
  }

  // Non-US flow: Steps 1-3 + Step 4 (RequestAccess), then redirect to /waitlist-sent
  if (data.country && data.country !== 'US') {
    return 4;
  }

  // US flow: Always use full 7-step flow with accreditation
  return 7;
};

/**
 * Default total steps for onboarding (used as fallback)
 */
export const DEFAULT_TOTAL_ONBOARDING_STEPS = 6;
